import React, { useEffect } from 'react';
import BezierEasing from 'bezier-easing';
import { Drawer, DrawerContent, DrawerHeader } from '@fiverr-private/overlay';
import { Text } from '@fiverr-private/typography';
import { getContext } from '@fiverr-private/fiverr_context';
import { Container } from '@fiverr-private/layout_components';
import { theme } from '@fiverr-private/theme';
import { EASING, NodeGroup } from '@fiverr-private/transition';
import { AppI18n } from '../../i18n';
import { useBriefCreationContext } from '../../context/BriefCreationContext';
import { Step } from '../../constants';
import { BriefCreation } from '../BriefCreation';
import { mixpanelEvents } from '../../monitoring';
import BriefCreationCloseButton from './BriefCreationCloseButton';
import { drawerContentClass } from './styles.ve.css';
import { useMixpanelRecording } from './useMixpanelRecording';

const defaultAnimationParams = {
  enter: {
    delay: 0,
    duration: 300,
    ease: EASING.easeIn,
  },
  exit: {
    duration: 300,
    ease: EASING.easeIn,
  },
};

const stepToAnimationParams = {
  [Step.Creation]: {
    enter: {
      duration: 200,
    },
    exit: {
      duration: 0,
    },
  },
  [Step.Loading]: {
    enter: {
      delay: 500,
      duration: 500,
      ease: BezierEasing(0.96, -0.01, 0, 1),
    },
    exit: {
      duration: 500,
      ease: BezierEasing(0.96, -0.01, 0, 1),
    },
  },
  [Step.Rephrase]: {
    enter: {
      duration: 0,
    },
  },
  [Step.Disambiguation]: {
    enter: {
      duration: 0,
    },
    exit: {
      duration: 0,
    },
  },
};

const getNearestOpacity = (value: number) =>
  Object.keys(theme.opacity).reduce((prev, curr) =>
    Math.abs(Number(curr) - value) < Math.abs(Number(prev) - value) ? curr : prev
  ) as keyof typeof theme.opacity;

const stepsToHideHeader = [Step.Finish, Step.ProMigration];

const stepToTitle = (step: Step) => {
  switch (step) {
    case Step.ProMigration:
    case Step.Finish:
    case Step.ComingSoon:
    case Step.Error: {
      return '';
    }
    case Step.Edit: {
      return <AppI18n k="drawer_header_editor" />;
    }
    default: {
      return <AppI18n k="drawer_header" />;
    }
  }
};

const stepToSize = {
  [Step.Edit]: 'xl',
  [Step.ComingSoon]: 'lg',
};

const BriefCreationDrawerComponent = () => {
  const { currentStep, isOpen, setIsOpen, setStep, initialStep, setUserInput, onOpen } = useBriefCreationContext();
  const { isMobile } = getContext();

  useMixpanelRecording();

  const handleAnimationEnd = () => {
    if (!isOpen) {
      setStep(initialStep);
      setUserInput('');
    } else {
      onOpen?.();
    }
  };

  useEffect(() => {
    if (isOpen) {
      mixpanelEvents.dmBriefCreationDrawerOpen();
    }
  }, [isOpen]);

  const getSizeProps = () =>
    ({
      size: stepToSize[currentStep] || 'md',
    } as const);

  return (
    <Drawer isOpen={isOpen} onClose={() => setIsOpen(false)} onOpen={() => setIsOpen(true)}>
      <NodeGroup
        data={[currentStep]}
        keyAccessor={(d) => d}
        start={() => ({ opacity: [0] })}
        enter={() => ({
          opacity: [1],
          timing: {
            ...defaultAnimationParams.enter,
            ...stepToAnimationParams[currentStep]?.enter,
          },
        })}
        leave={() => ({
          opacity: [0],
          timing: {
            ...defaultAnimationParams.exit,
            ...stepToAnimationParams[currentStep]?.exit,
          },
        })}
        update={() => ({
          opacity: [1],
          timing: {
            ...defaultAnimationParams.enter,
            ...stepToAnimationParams[currentStep]?.enter,
          },
        })}
      >
        {(nodes) => (
          <Container className={currentStep > 2 && isOpen ? drawerContentClass : ''}>
            <DrawerContent
              closeOnOverlayClick={false}
              closeOnEscapeKey={false}
              onAnimationEnd={handleAnimationEnd}
              {...getSizeProps()}
            >
              {nodes.map(({ key, state: { opacity } }) => (
                <Container
                  key={key}
                  opacity={getNearestOpacity(opacity)}
                  flex={1}
                  direction="column"
                  position="absolute"
                  height="100%"
                  width="100%"
                  display={key === Step.Edit && opacity === 0 ? 'none' : 'flex'}
                >
                  {!stepsToHideHeader.includes(key) && !(isMobile && key === Step.Edit) && (
                    <DrawerHeader>
                      <Text size="b_lg" fontWeight="bold">
                        {stepToTitle(key)}
                      </Text>
                      <BriefCreationCloseButton />
                    </DrawerHeader>
                  )}
                  <BriefCreation />
                </Container>
              ))}
            </DrawerContent>
          </Container>
        )}
      </NodeGroup>
    </Drawer>
  );
};

export default BriefCreationDrawerComponent;
export { LazyBriefCreationDrawerComponent } from './LazyBriefCreationDrawerComponent';
